import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { 
  DollarSign,
  Search,
  Filter,
  TrendingUp,
  TrendingDown,
  Loader2,
  AlertCircle,
  Edit3,
  Check,
  X,
  Percent
} from 'lucide-react';
import { 
  adminApi,
  type FormularyListItem,
  type PharmacyListItem,
  type PricingUpdateRequest,
  FORMULARY_CATEGORIES,
  PHARMACY_NAMES
} from '@/services/adminService';

interface PricingData {
  formularyId: string
  formularyName: string
  category: string
  pharmacyPrices: Record<string, { price: number; available: boolean; pharmacyName: string }>
}

const PricingMatrix = () => {
  const [pricingData, setPricingData] = useState<PricingData[]>([]);
  const [pharmacies, setPharmacies] = useState<PharmacyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  
  // Editing states
  const [editingCell, setEditingCell] = useState<string | null>(null); // formularyId-pharmacyId
  const [editingPrice, setEditingPrice] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  
  // Bulk update states
  const [showBulkUpdateDialog, setShowBulkUpdateDialog] = useState(false);
  const [bulkUpdateType, setBulkUpdateType] = useState<'percentage' | 'amount'>('percentage');
  const [bulkUpdateValue, setBulkUpdateValue] = useState<string>('');
  const [bulkUpdateDirection, setBulkUpdateDirection] = useState<'increase' | 'decrease'>('increase');

  // Load data
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load formularies and pharmacies in parallel
      const [formulariesResponse, pharmaciesResponse] = await Promise.all([
        adminApi.getFormularies({
          name: searchTerm ? [searchTerm] : undefined,
          category: selectedCategory !== 'all' ? [selectedCategory] : undefined
        }),
        adminApi.getPharmacies()
      ]);
      
      setPharmacies(pharmaciesResponse.items);
      
      // Transform formulary data into pricing matrix format
      const pricingMatrix: PricingData[] = formulariesResponse.items.map(formulary => ({
        formularyId: formulary.id,
        formularyName: formulary.name,
        category: formulary.category,
        pharmacyPrices: formulary.pharmacies.reduce((acc, pharmacy) => {
          acc[pharmacy.id] = {
            price: pharmacy.price,
            available: pharmacy.available,
            pharmacyName: pharmacy.name
          };
          return acc;
        }, {} as Record<string, { price: number; available: boolean; pharmacyName: string }>)
      }));
      
      setPricingData(pricingMatrix);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [searchTerm, selectedCategory]);

  // Handle inline price editing
  const handleEditStart = (formularyId: string, pharmacyId: string, currentPrice: number) => {
    const cellId = `${formularyId}-${pharmacyId}`;
    setEditingCell(cellId);
    setEditingPrice(currentPrice.toString());
  };

  const handleEditSave = async (formularyId: string, pharmacyId: string) => {
    const newPrice = parseFloat(editingPrice);
    if (isNaN(newPrice) || newPrice < 0) {
      setError('Please enter a valid price');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      await adminApi.updatePricing({
        pharmacyId,
        formularyId,
        price: newPrice
      });
      
      // Update local state
      setPricingData(prev => prev.map(item => {
        if (item.formularyId === formularyId) {
          return {
            ...item,
            pharmacyPrices: {
              ...item.pharmacyPrices,
              [pharmacyId]: {
                ...item.pharmacyPrices[pharmacyId],
                price: newPrice
              }
            }
          };
        }
        return item;
      }));
      
      setEditingCell(null);
      setEditingPrice('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update price');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditCancel = () => {
    setEditingCell(null);
    setEditingPrice('');
  };

  // Handle bulk price update
  const handleBulkUpdate = async () => {
    const updateValue = parseFloat(bulkUpdateValue);
    if (isNaN(updateValue) || updateValue <= 0) {
      setError('Please enter a valid update value');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      // Filter data based on current search/category filters
      const filteredData = pricingData.filter(item => {
        const matchesSearch = !searchTerm || item.formularyName.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
        return matchesSearch && matchesCategory;
      });
      
      // Calculate bulk updates
      const updates: PricingUpdateRequest[] = [];
      
      filteredData.forEach(formulary => {
        Object.entries(formulary.pharmacyPrices).forEach(([pharmacyId, priceInfo]) => {
          if (priceInfo.available) {
            let newPrice: number;
            
            if (bulkUpdateType === 'percentage') {
              const multiplier = bulkUpdateDirection === 'increase' 
                ? (1 + updateValue / 100) 
                : (1 - updateValue / 100);
              newPrice = priceInfo.price * multiplier;
            } else {
              newPrice = bulkUpdateDirection === 'increase' 
                ? priceInfo.price + updateValue 
                : priceInfo.price - updateValue;
            }
            
            // Ensure minimum price of $0.01
            newPrice = Math.max(0.01, Math.round(newPrice * 100) / 100);
            
            updates.push({
              pharmacyId,
              formularyId: formulary.formularyId,
              price: newPrice
            });
          }
        });
      });
      
      // Execute updates (in a real app, you might want to batch these)
      await Promise.all(updates.map(update => adminApi.updatePricing(update)));
      
      // Reload data to reflect changes
      await loadData();
      
      setShowBulkUpdateDialog(false);
      setBulkUpdateValue('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to perform bulk update');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-syne font-medium text-vitl-navy mb-2">Pricing Matrix</h2>
          <p className="text-vitl-dark/70 font-lato">Manage medication pricing across pharmacy partners</p>
        </div>
        <Dialog open={showBulkUpdateDialog} onOpenChange={setShowBulkUpdateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white font-lato font-medium">
              <Percent className="w-4 h-4 mr-2" />
              Bulk Update
            </Button>
          </DialogTrigger>
        </Dialog>
      </div>

      {/* Search and Filters */}
      <Card className="border-l-4 border-l-emerald-500 shadow-sm">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-vitl-dark/50" />
              <Input
                placeholder="Search formularies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 font-lato"
              />
            </div>
            
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="font-lato">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {FORMULARY_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex items-center text-sm text-vitl-dark/70 font-lato">
              <DollarSign className="w-4 h-4 mr-2" />
              {pricingData.length} formularies × {pharmacies.length} pharmacies
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
            <p className="text-red-700 font-lato text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      )}

      {/* Pricing Matrix */}
      {!loading && (
        <Card className="border-l-4 border-l-emerald-500 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-syne font-medium text-vitl-navy flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-emerald-600" />
              Pricing Matrix
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pricingData.length === 0 ? (
              <div className="text-center py-12">
                <DollarSign className="w-12 h-12 text-vitl-dark/30 mx-auto mb-4" />
                <p className="text-vitl-dark/70 font-lato">No pricing data found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-lato font-medium sticky left-0 bg-white z-10 min-w-[200px]">
                        Formulary
                      </TableHead>
                      <TableHead className="font-lato font-medium">Category</TableHead>
                      {pharmacies.map((pharmacy) => (
                        <TableHead key={pharmacy.id} className="font-lato font-medium text-center min-w-[120px]">
                          {pharmacy.name}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pricingData.map((formulary) => (
                      <TableRow key={formulary.formularyId} className="hover:bg-gray-50">
                        <TableCell className="font-medium font-lato text-vitl-navy sticky left-0 bg-white z-10">
                          {formulary.formularyName}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 font-lato text-xs">
                            {formulary.category}
                          </Badge>
                        </TableCell>
                        {pharmacies.map((pharmacy) => {
                          const priceInfo = formulary.pharmacyPrices[pharmacy.id];
                          const cellId = `${formulary.formularyId}-${pharmacy.id}`;
                          const isEditing = editingCell === cellId;
                          
                          return (
                            <TableCell key={pharmacy.id} className="text-center">
                              {priceInfo ? (
                                <div className="space-y-1">
                                  {isEditing ? (
                                    <div className="flex items-center justify-center space-x-1">
                                      <Input
                                        value={editingPrice}
                                        onChange={(e) => setEditingPrice(e.target.value)}
                                        className="w-20 h-8 text-xs text-center font-lato"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        autoFocus
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            handleEditSave(formulary.formularyId, pharmacy.id);
                                          } else if (e.key === 'Escape') {
                                            handleEditCancel();
                                          }
                                        }}
                                      />
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 w-8 p-0"
                                        onClick={() => handleEditSave(formulary.formularyId, pharmacy.id)}
                                        disabled={submitting}
                                      >
                                        <Check className="w-3 h-3 text-green-600" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 w-8 p-0"
                                        onClick={handleEditCancel}
                                      >
                                        <X className="w-3 h-3 text-red-600" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <div 
                                      className="cursor-pointer hover:bg-gray-100 rounded px-2 py-1 group"
                                      onClick={() => handleEditStart(formulary.formularyId, pharmacy.id, priceInfo.price)}
                                    >
                                      <div className="flex items-center justify-center space-x-1">
                                        <span className="font-lato font-medium text-vitl-navy">
                                          ${priceInfo.price.toFixed(2)}
                                        </span>
                                        <Edit3 className="w-3 h-3 text-vitl-dark/40 opacity-0 group-hover:opacity-100 transition-opacity" />
                                      </div>
                                    </div>
                                  )}
                                  {!priceInfo.available && (
                                    <Badge variant="secondary" className="text-xs">
                                      Unavailable
                                    </Badge>
                                  )}
                                </div>
                              ) : (
                                <span className="text-vitl-dark/40 font-lato text-sm">—</span>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Bulk Update Dialog */}
      <Dialog open={showBulkUpdateDialog} onOpenChange={setShowBulkUpdateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-syne font-medium text-vitl-navy">
              Bulk Price Update
            </DialogTitle>
            <DialogDescription className="font-lato text-vitl-dark/70">
              Apply price changes to all visible formularies
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                Update Type
              </label>
              <Select value={bulkUpdateType} onValueChange={(value: 'percentage' | 'amount') => setBulkUpdateType(value)}>
                <SelectTrigger className="font-lato">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage</SelectItem>
                  <SelectItem value="amount">Fixed Amount</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                Direction
              </label>
              <Select value={bulkUpdateDirection} onValueChange={(value: 'increase' | 'decrease') => setBulkUpdateDirection(value)}>
                <SelectTrigger className="font-lato">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="increase">
                    <div className="flex items-center">
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Increase
                    </div>
                  </SelectItem>
                  <SelectItem value="decrease">
                    <div className="flex items-center">
                      <TrendingDown className="w-4 h-4 mr-2" />
                      Decrease
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                {bulkUpdateType === 'percentage' ? 'Percentage (%)' : 'Amount ($)'}
              </label>
              <Input
                type="number"
                value={bulkUpdateValue}
                onChange={(e) => setBulkUpdateValue(e.target.value)}
                placeholder={bulkUpdateType === 'percentage' ? '10' : '5.00'}
                className="font-lato"
                min="0"
                step={bulkUpdateType === 'percentage' ? '1' : '0.01'}
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center">
                  <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                  <p className="text-red-700 font-lato text-sm">{error}</p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowBulkUpdateDialog(false);
                setBulkUpdateValue('');
                setError(null);
              }}
              className="font-lato"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkUpdate}
              disabled={submitting || !bulkUpdateValue}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-lato font-medium"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Apply Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PricingMatrix;